/**
 * /customer-duplicates API client.
 *
 * The list call uses a raw fetch (same reason as arDunningApi/docuwarePoCheckApi: the pagination
 * block carries `total`, and `fetchApi` unwraps only `data`). Detail and decide go through
 * `fetchApi`. Export downloads a file (same `downloadFile` pattern as `arDunningApi.ts`).
 */
import { fetchApi } from './api';
import type {
    PairListResult, PairListSummary, PairRow, PairFilters, PairSortField, PairDetail, DecideRequest, DecisionStatus,
    GroupListResult, GroupListSummary, GroupRow, GroupFilters, GroupSortField, GroupDetail, GroupForCustomer,
    DuplicateAttentionSummary,
} from '../types/customerDuplicates';

const BASE_URL = '/api/v1';

const EMPTY_SUMMARY: PairListSummary = {
    byReason: {
        BLOCKED_REPLACEMENT: { total: 0, bothDormant: 0, oneSideDormant: 0 },
        MIGRATION: { total: 0, bothDormant: 0, oneSideDormant: 0 },
        VAT: { total: 0, bothDormant: 0, oneSideDormant: 0 },
        BUSINESS_UNIT: { total: 0, bothDormant: 0, oneSideDormant: 0 },
        SITE: { total: 0, bothDormant: 0, oneSideDormant: 0 },
        UNCLEAR: { total: 0, bothDormant: 0, oneSideDormant: 0 },
    },
    byCompany: {}, byLevel: {}, byStatus: {},
    affectedAccounts: 0, openItems: 0, openAmountEur: 0, accountsWithOpenItems: 0,
    revenueEur: 0, revenueWindowMonths: 24, accountsWithRevenue: 0,
    latestRun: null,
    registryCoverage: { checked: 0, total: 0 },
    goneNotDecided: 0,
};

export const EMPTY_GROUP_SUMMARY: GroupListSummary = {
    totals: {
        groups: 0, members: 0, multiCompanyGroups: 0, openItems: 0, openAmountEur: 0,
        overdueAmountEur: 0, revenueEur: 0, hubspotCompanies: 0, blockedMembers: 0,
    },
    warning: { anyWarning: 0, blockedSplit: 0, overdueSplit: 0, noWarning: 0, warningOpenAmountEur: 0 },
    byCompany: {},
    config: {
        overdueDays: 60, minOpenAmountEur: 100, requireActiveCounterpart: true,
        inactivityMonths: 24, revenueWindowMonths: 24,
    },
    lastBuild: null,
};

interface ListParams {
    page: number;
    limit: number;
    filters: PairFilters;
    sortBy: PairSortField;
    sortOrder: 'asc' | 'desc';
}

/** Unwrap the house envelope, or throw with the server's own message. */
async function unwrap<T>(res: Response): Promise<{ data: T; total: number }> {
    const body = await res.json();
    if (!res.ok || !body.success) {
        throw new Error(body.error?.message || `API error: ${res.status}`);
    }
    return { data: body.data as T, total: body.pagination?.total ?? 0 };
}

function filterParams(filters: PairFilters): Record<string, string> {
    const out: Record<string, string> = {};
    if (filters.reason) out.reason = filters.reason;
    if (filters.company) out.company = filters.company;
    if (filters.level) out.level = filters.level;
    if (filters.scope) out.scope = filters.scope;
    if (filters.marker) out.marker = filters.marker;
    if (filters.status) out.status = filters.status;
    if (filters.onlyNew) out.onlyNew = '1';
    // Only sent when it is NOT the default, so an unchanged URL stays as short as it was.
    if (filters.presence && filters.presence !== 'CURRENT') out.presence = filters.presence;
    if (filters.search) out.search = filters.search;
    return out;
}

export const customerDuplicatesApi = {
    async list(params: ListParams): Promise<PairListResult> {
        const sp = new URLSearchParams();
        sp.set('page', String(params.page));
        sp.set('limit', String(params.limit));
        sp.set('sortBy', params.sortBy);
        sp.set('sortOrder', params.sortOrder);
        for (const [key, value] of Object.entries(filterParams(params.filters))) {
            sp.set(key, value);
        }

        const res = await fetch(`${BASE_URL}/customer-duplicates/pairs?${sp}`, {
            headers: { 'Content-Type': 'application/json' },
        });
        const { data, total } = await unwrap<{ items: PairRow[]; summary: PairListSummary }>(res);
        return {
            items: data?.items ?? [],
            summary: { ...EMPTY_SUMMARY, ...(data?.summary ?? {}) },
            total,
        };
    },

    async detail(pairKey: string): Promise<PairDetail> {
        return fetchApi<PairDetail>(`/customer-duplicates/pairs/${encodeURIComponent(pairKey)}`);
    },

    async decide(pairKey: string, body: DecideRequest): Promise<{ status: DecisionStatus }> {
        return fetchApi(`/customer-duplicates/pairs/${encodeURIComponent(pairKey)}/decide`, {
            method: 'POST',
            body: JSON.stringify(body),
        });
    },

    // --- P4: the group-wide identity ---------------------------------------------------------

    async listGroups(params: {
        page: number; limit: number; filters: GroupFilters;
        sortBy: GroupSortField; sortOrder: 'asc' | 'desc';
    }): Promise<GroupListResult> {
        const sp = new URLSearchParams();
        sp.set('page', String(params.page));
        sp.set('limit', String(params.limit));
        sp.set('sortBy', params.sortBy);
        sp.set('sortOrder', params.sortOrder);
        if (params.filters.company) sp.set('company', params.filters.company);
        if (params.filters.warning) sp.set('warning', params.filters.warning);
        if (params.filters.multiCompany) sp.set('multiCompany', '1');
        if (params.filters.search) sp.set('search', params.filters.search);

        const res = await fetch(`${BASE_URL}/customer-duplicates/groups?${sp}`, {
            headers: { 'Content-Type': 'application/json' },
        });
        const { data, total } = await unwrap<{ items: GroupRow[]; summary: GroupListSummary }>(res);
        return {
            items: data?.items ?? [],
            summary: { ...EMPTY_GROUP_SUMMARY, ...(data?.summary ?? {}) },
            total,
        };
    },

    async groupDetail(groupId: number): Promise<GroupDetail> {
        return fetchApi<GroupDetail>(`/customer-duplicates/groups/${groupId}`);
    },

    /**
     * Resolves a customer to its group. Gated on /customer-duplicates like every other call here,
     * so most users get a 403 — which is why the caller treats ANY failure as "render nothing"
     * rather than as an error worth showing.
     */
    async groupForCustomer(customerId: string): Promise<GroupForCustomer> {
        return fetchApi<GroupForCustomer>(
            `/customer-duplicates/groups/by-customer/${encodeURIComponent(customerId)}`,
        );
    },

    async export(filters: PairFilters): Promise<void> {
        const sp = new URLSearchParams(filterParams(filters));
        const res = await fetch(`${BASE_URL}/customer-duplicates/export?${sp}`);
        if (res.status === 401) {
            const { redirectToLogin } = await import('../utils/auth-redirect');
            redirectToLogin();
            throw new Error('Session expired');
        }
        if (!res.ok) {
            let message = `Export failed (status ${res.status})`;
            try {
                const body = await res.json() as { error?: { message?: string } };
                if (body.error?.message) message = body.error.message;
            } catch { /* non-JSON error page */ }
            throw new Error(message);
        }
        const disposition = res.headers.get('content-disposition') || '';
        const match = /filename="?([^"]+)"?/.exec(disposition);
        const filename = match?.[1] || 'CustomerDuplicates.xlsx';
        const blob = await res.blob();
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objectUrl);
    },

    /**
     * What the navigation badge counts (P5). Cheap by design — it is fetched on every page load
     * of the app, so it never touches the AR or revenue aggregates.
     */
    async attentionSummary(): Promise<DuplicateAttentionSummary> {
        const res = await fetch(`${BASE_URL}/customer-duplicates/attention-summary`, {
            headers: { 'Content-Type': 'application/json' },
        });
        const { data } = await unwrap<DuplicateAttentionSummary>(res);
        return data;
    },
};
